import { useCallback, useEffect, useRef, useState } from 'react'

import { GatewayRpcClient } from '@modules/equabot-settings/services/gateway-rpc'

import type {
  ConnectionStatus,
  OrgGatewayEndpoint,
  OrgGatewayResolver,
  OrgGatewayState,
} from '../types/admin'
import { reportRpcError } from '../services/report-rpc-error'

export type UseOrgGatewayRpcInput = {
  /** The currently active organization id, or null/undefined if none. */
  organizationId: string | null | undefined
  /** Maps an org id to its gateway endpoint. See spike-ws-org-binding.md. */
  resolver: OrgGatewayResolver
}

export type UseOrgGatewayRpcReturn = OrgGatewayState & {
  /** The live RPC client for the active org, or null until connected. */
  client: GatewayRpcClient | null
  /**
   * Invoke a JSON-RPC method bound to the active org. Rejects with a normalized
   * error and emits a telemetry report (NFR-004) when the gateway returns ok:false.
   */
  call: <T = unknown>(method: string, params?: unknown) => Promise<T>
}

const INITIAL_STATE: OrgGatewayState = {
  status: 'disconnected',
  organizationId: null,
  endpoint: null,
  lastError: null,
  missingConfig: false,
}

/**
 * FR-021: binds a `GatewayRpcClient` to the currently active organization.
 *
 * - When `organizationId` changes, any prior client is torn down before a new
 *   client is created — no connection context is carried across orgs.
 * - When `resolver` returns `null` for the active org, `missingConfig` flips to
 *   `true` and no client is created; `AdminShell` renders the empty state.
 * - The hook does not read `localStorage`; all credentials come from the
 *   resolver, which is wired to equa-server by a parent component.
 */
export function useOrgGatewayRpc({
  organizationId,
  resolver,
}: UseOrgGatewayRpcInput): UseOrgGatewayRpcReturn {
  const [state, setState] = useState<OrgGatewayState>(INITIAL_STATE)
  const clientRef = useRef<GatewayRpcClient | null>(null)
  const mountedRef = useRef(true)
  const activeOrgRef = useRef<string | null>(null)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      if (clientRef.current) {
        clientRef.current.disconnect()
        clientRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    const nextOrg = organizationId ?? null
    activeOrgRef.current = nextOrg

    // Always tear down any prior client on org change (FR-021: no carry-over).
    if (clientRef.current) {
      clientRef.current.disconnect()
      clientRef.current = null
    }

    if (!nextOrg) {
      setState({ ...INITIAL_STATE, organizationId: null })
      return
    }

    setState({
      status: 'connecting',
      organizationId: nextOrg,
      endpoint: null,
      lastError: null,
      missingConfig: false,
    })

    let cancelled = false

    void resolver(nextOrg)
      .then((endpoint: OrgGatewayEndpoint) => {
        if (cancelled || !mountedRef.current) return
        if (activeOrgRef.current !== nextOrg) return

        if (!endpoint) {
          setState({
            status: 'disconnected',
            organizationId: nextOrg,
            endpoint: null,
            lastError: null,
            missingConfig: true,
          })
          return
        }

        const client = new GatewayRpcClient()
        clientRef.current = client

        client.onConnectionChange((status: ConnectionStatus, err?: string) => {
          if (!mountedRef.current) return
          if (activeOrgRef.current !== nextOrg) return
          setState((prev) => ({
            ...prev,
            status,
            lastError: status === 'error' ? (err ?? 'Gateway connection error') : null,
          }))
        })

        setState({
          status: 'connecting',
          organizationId: nextOrg,
          endpoint,
          lastError: null,
          missingConfig: false,
        })

        client.connect(endpoint.url, endpoint.token, undefined)
      })
      .catch((err: unknown) => {
        if (cancelled || !mountedRef.current) return
        if (activeOrgRef.current !== nextOrg) return
        const message = err instanceof Error ? err.message : String(err)
        reportRpcError(err, {
          method: 'resolver',
          params: { organizationId: nextOrg },
          organizationId: nextOrg,
        })
        setState({
          status: 'error',
          organizationId: nextOrg,
          endpoint: null,
          lastError: message,
          missingConfig: false,
        })
      })

    return () => {
      cancelled = true
    }
  }, [organizationId, resolver])

  const call = useCallback(async <T,>(method: string, params?: unknown): Promise<T> => {
    const client = clientRef.current
    const orgId = activeOrgRef.current
    if (!client || !client.connected) {
      const err = new Error('gateway not connected')
      reportRpcError(err, { method, params, organizationId: orgId })
      throw err
    }
    try {
      return await client.call<T>(method, params)
    } catch (err) {
      reportRpcError(err, { method, params, organizationId: orgId })
      throw err
    }
  }, [])

  return {
    ...state,
    client: clientRef.current,
    call,
  }
}
